/**
 * Tab „Team-Übersicht“ (/urlaub): Monats-Matrix Mitarbeiter × Tage aus
 * GET /leave-requests/team-overview — Grund-Farben aus lib/absenceColors,
 * Wochenenden schattiert (Muster: MonthCalendar der Abwesenheiten-Seite).
 */

import { useMemo, useState } from "react"
import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { DAY_TYPE_TINT } from "@/components/calendar/dayTypes"
import { LoadingSpinner } from "@/components/shared"
import { Card, CardContent } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { ABSENCE_REASONS, ABSENCE_REASON_VALUES, ABSENCE_STATUS_LABEL } from "@/lib/absenceColors"
import { toDayMap, useDayInfos } from "@/lib/useDayInfos"
import { cn } from "@/lib/utils"
import { api } from "@/services/api"
import type { TeamOverviewDay } from "@/types"

const MONTH_NAMES = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
]

const pad = (n: number) => String(n).padStart(2, "0")

function monthDays(year: number, month: number): { iso: string; day: number; isWeekend: boolean }[] {
  const count = new Date(year, month, 0).getDate()
  return Array.from({ length: count }, (_, i) => {
    const day = i + 1
    const weekday = new Date(year, month - 1, day).getDay()
    return {
      iso: `${year}-${pad(month)}-${pad(day)}`,
      day,
      isWeekend: weekday === 0 || weekday === 6,
    }
  })
}

export function TeamOverviewTab() {
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)

  const query = useQuery({
    queryKey: ["leave", "team-overview", year, month],
    queryFn: () => api.leave.teamOverview(year, month),
    placeholderData: keepPreviousData,
  })

  const days = useMemo(() => monthDays(year, month), [year, month])
  const employees = query.data?.employees ?? []

  // Tages-Hintergruende aus dem Betriebskalender (EIN dayInfos-Call pro Monat)
  const dayInfos = useDayInfos(year, month)
  const dayMap = useMemo(() => toDayMap(dayInfos.data), [dayInfos.data])

  return (
    <div className="space-y-4">
      {/* Monat/Jahr-Wahl */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-40 space-y-1.5">
          <Label>Monat</Label>
          <Select value={String(month)} onChange={(e) => setMonth(Number(e.target.value))}>
            {MONTH_NAMES.map((name, i) => (
              <option key={name} value={i + 1}>
                {name}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-32 space-y-1.5">
          <Label>Jahr</Label>
          <Select value={String(year)} onChange={(e) => setYear(Number(e.target.value))}>
            {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1, now.getFullYear() + 2].map(
              (y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              )
            )}
          </Select>
        </div>
      </div>

      <Card>
        <CardContent className="overflow-x-auto py-4">
          {query.isLoading ? (
            <LoadingSpinner fullPage />
          ) : employees.length === 0 ? (
            <p className="text-sm text-muted-foreground">Keine sichtbaren Mitarbeiter.</p>
          ) : (
            <div className="min-w-[720px] space-y-1">
              {/* Kopfzeile: Tagesnummern */}
              <div
                className="grid gap-px text-center text-[10px] text-muted-foreground"
                style={{ gridTemplateColumns: `10rem repeat(${days.length}, minmax(0, 1fr))` }}
              >
                <div />
                {days.map((d) => {
                  const info = dayMap.get(d.iso)
                  const offDay = info ? !info.isPlannable : d.isWeekend
                  return (
                    <div
                      key={d.iso}
                      title={info?.holidayName ?? undefined}
                      className={cn(offDay && "text-muted-foreground/50")}
                    >
                      {d.day}
                    </div>
                  )
                })}
              </div>
              {/* Zeile je Mitarbeiter */}
              {employees.map((row) => {
                const byDate = new Map<string, TeamOverviewDay>(
                  row.days.map((d) => [d.date, d])
                )
                return (
                  <div
                    key={row.employeeId}
                    className="grid items-center gap-px"
                    style={{ gridTemplateColumns: `10rem repeat(${days.length}, minmax(0, 1fr))` }}
                  >
                    <div className="truncate pr-2 text-xs font-medium">{row.employeeName}</div>
                    {days.map((d) => {
                      const entry = byDate.get(d.iso)
                      const info = dayMap.get(d.iso)
                      const tint = info
                        ? DAY_TYPE_TINT[info.dayType]
                        : d.isWeekend
                          ? "bg-muted/60"
                          : "bg-muted/25"
                      return (
                        <div
                          key={d.iso}
                          className={cn(
                            "h-5 rounded-sm",
                            entry
                              ? cn(
                                  ABSENCE_REASONS[entry.reason].bar,
                                  entry.status === "PENDING" && "opacity-50"
                                )
                              : tint
                          )}
                          title={
                            entry
                              ? `${row.employeeName}: ${ABSENCE_REASONS[entry.reason].label}` +
                                ` (${ABSENCE_STATUS_LABEL[entry.status]})`
                              : (info?.holidayName ?? undefined)
                          }
                        />
                      )
                    })}
                  </div>
                )
              })}
              {/* Legende */}
              <div className="flex flex-wrap gap-3 pt-3">
                {ABSENCE_REASON_VALUES.map((r) => (
                  <span key={r} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className={cn("size-3 rounded-sm", ABSENCE_REASONS[r].bar)} />
                    {ABSENCE_REASONS[r].label}
                  </span>
                ))}
                {(
                  [
                    ["FEIERTAG", "Feiertag"],
                    ["BETRIEBSURLAUB", "Betriebsurlaub"],
                    ["WOCHENENDE", "Wochenende"],
                  ] as const
                ).map(([t, label]) => (
                  <span
                    key={t}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground"
                  >
                    <span className={cn("size-3 rounded-sm border", DAY_TYPE_TINT[t])} />
                    {label}
                  </span>
                ))}
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="size-3 rounded-sm bg-blue-500 opacity-50" />
                  halbtransparent = beantragt
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
